import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  MutableRefObject,
} from 'react';
import { ArrowRight, Loader2, Copy, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { marked } from 'marked';
import ChatMarkdown, { sanitizeChatHtml } from './ChatMarkdown';
import { apiService, BriefBuilderMessage } from '../services/api';
import DocumentUploadBar from './DocumentUploadBar';
import { UserProfile } from './UserProfile';
import { UserProfileEditor } from './UserProfileEditor';
import { useAuth } from '../hooks/useAuth';
import { getAgentLanguage } from '../utils/agentLanguage';

interface BriefBuilderPanelProps {
  onLaunchWorkspace: (options: { briefId?: string | null; finalPrompt: string }) => Promise<void>;
  isLaunching: boolean;
  seedPrompt?: string | null;
  hideInput?: boolean;
  agentId?: string | null;
  renderActions?: (actions: ReactNode) => ReactNode;
  renderSidebar?: (sidebar: ReactNode) => ReactNode;
  onConversationStart?: () => void;
  onCompetitorStart?: () => void;
  onOutputStart?: () => void;
  sessionId?: string | null;
  ensureSessionId?: () => Promise<string | null>;
  onDocumentsBusyChange?: (busy: boolean) => void;
}

const PROMPT_START_TAG = '[Prompt_Brief_For_Research]';
const PROMPT_END_TAG = '[Prompt_End]';

type StructuredBlockParseResult = {
  cleanedContent: string;
  contexts: string[];
  competitors: string[];
  outputs: string[];
};

const tightenStructuredBlock = (block: string): string => {
  if (!block) return '';

  const normalized = block.replace(/\r\n/g, '\n');
  const lines = normalized.split('\n');
  const tightened: string[] = [];
  let pendingBlank = false;

  lines.forEach((rawLine) => {
    const trimmedRight = rawLine.replace(/[ \t]+$/g, '');
    if (!trimmedRight.trim()) {
      pendingBlank = tightened.length > 0;
      return;
    }

    if (pendingBlank) {
      tightened.push('');
      pendingBlank = false;
    }

    tightened.push(trimmedRight);
  });

  return tightened.join('\n');
};

const extractStructuredBlocks = (input: string): StructuredBlockParseResult => {
  if (!input) {
    return { cleanedContent: '', contexts: [], competitors: [], outputs: [] };
  }

  const contexts: string[] = [];
  const competitors: string[] = [];
  const outputs: string[] = [];

  const contextRegex = /\[Context_Start\]([\s\S]*?)\[Context_End\]/gi;
  const competitorRegex = /\[Competitor_Start\]([\s\S]*?)\[Competitor_End\]/gi;
  const outputRegex = /\[Output_Start\]([\s\S]*?)\[Output_End\]/gi;

  let working = input;

  working = working.replace(contextRegex, (_match, inner) => {
    const text = tightenStructuredBlock(String(inner).trim());
    if (text) contexts.push(text);
    return '\n\n';
  });

  working = working.replace(competitorRegex, (_match, inner) => {
    const text = tightenStructuredBlock(String(inner).trim());
    if (text) competitors.push(text);
    return '\n\n';
  });

  working = working.replace(outputRegex, (_match, inner) => {
    const text = tightenStructuredBlock(String(inner).trim());
    if (text) outputs.push(text);
    return '\n\n';
  });

  const cleanedContent = working
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

  return {
    cleanedContent,
    contexts,
    competitors,
    outputs,
  };
};

const arraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const tokenizeForDiff = (text: string): string[] => {
  if (!text) return [];
  return text.split(/(\s+)/);
};

const highlightDiffText = (nextText: string, prevText: string): string => {
  if (!nextText) return '';
  if (nextText === prevText) return nextText;

  const prevTokens = tokenizeForDiff(prevText);
  if (prevTokens.length === 0) {
    return `<mark>${nextText}</mark>`;
  }
  const nextTokens = tokenizeForDiff(nextText);

  const prevLen = prevTokens.length;
  const nextLen = nextTokens.length;
  const dp: number[][] = Array.from({ length: prevLen + 1 }, () =>
    Array(nextLen + 1).fill(0)
  );

  for (let i = prevLen - 1; i >= 0; i -= 1) {
    for (let j = nextLen - 1; j >= 0; j -= 1) {
      if (prevTokens[i] === nextTokens[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const flushAdded = (buffer: string[], acc: string[]) => {
    if (!buffer.length) return;
    const chunk = buffer.join('');
    if (chunk.trim()) {
      acc.push(`<mark>${chunk}</mark>`);
    } else {
      acc.push(chunk);
    }
    buffer.length = 0;
  };

  const result: string[] = [];
  const addedBuffer: string[] = [];
  let i = 0;
  let j = 0;

  while (i < prevLen && j < nextLen) {
    if (prevTokens[i] === nextTokens[j]) {
      flushAdded(addedBuffer, result);
      result.push(nextTokens[j]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      addedBuffer.push(nextTokens[j]);
      j += 1;
    }
  }

  while (j < nextLen) {
    addedBuffer.push(nextTokens[j]);
    j += 1;
  }

  flushAdded(addedBuffer, result);
  const highlighted = result.join('');
  return highlighted.includes('<mark>') ? highlighted : nextText;
};

const useDiffHighlights = (blocks: string[], resetKey?: any) => {
  const previousRef = useRef<string[]>([]);
  const hasBaselineRef = useRef(false);

  const highlighted = useMemo(() => {
    if (!hasBaselineRef.current) {
      return blocks;
    }
    return blocks.map((block, idx) =>
      highlightDiffText(block, previousRef.current[idx] || '')
    );
  }, [blocks]);

  useEffect(() => {
    // External reset (e.g., when moving to another block)
    if (resetKey !== undefined) {
      previousRef.current = [];
      hasBaselineRef.current = false;
    }
    if (blocks.length === 0) {
      previousRef.current = [];
      hasBaselineRef.current = false;
      return;
    }
    previousRef.current = [...blocks];
    if (!hasBaselineRef.current) {
      hasBaselineRef.current = true;
    }
  }, [blocks]);

  return highlighted;
};

const pushHistorySnapshot = (ref: MutableRefObject<string[][]>, blocks: string[]) => {
  if (!blocks.length) return;
  ref.current.push([...blocks]);
};

const sanitizeHtml = sanitizeChatHtml;

const transformCompetitorHtml = (html: string): string => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const table = doc.querySelector('table');
    if (!table) return html;

    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) return html;

    const headers = Array.from(rows[0].querySelectorAll('th')).map((th) => (th.textContent || '').trim());
    const lowerHeaders = headers.map((h) => h.toLowerCase());
    const catIdx = lowerHeaders.findIndex((h) => h.includes('category'));
    if (catIdx === -1) return html;

    const keepIdx = headers
      .map((_, idx) => idx)
      .filter((idx) => idx !== catIdx)
      .slice(0, 3);
    if (!keepIdx.length) return html;

    const keepHeaders = keepIdx.map((idx) => headers[idx] || `Col ${idx + 1}`);

    const byCategory: Record<string, string[][]> = {};
    const byCategoryHtml: Record<string, string[][]> = {};

    rows.slice(1).forEach((row) => {
      const cells = Array.from(row.querySelectorAll('td'));
      if (!cells.length) return;
      const cat = (cells[catIdx]?.textContent || '').trim() || 'Category';
      const kept = keepIdx.map((idx) => (cells[idx]?.textContent || '').trim());
      const keptHtml = keepIdx.map((idx) => (cells[idx]?.innerHTML || '').trim());
      const hasContent = kept.some((v) => v);
      if (!hasContent) return;
      if (!byCategory[cat]) byCategory[cat] = [];
      if (!byCategoryHtml[cat]) byCategoryHtml[cat] = [];
      byCategory[cat].push(kept);
      byCategoryHtml[cat].push(keptHtml);
    });

    const fragment = doc.createElement('div');
    Object.entries(byCategory)
      .slice(0, 4)
      .forEach(([cat, rowsForCat], idx) => {
        const title = doc.createElement('div');
        title.className = 'competitor-category-title';
        title.textContent = `Category ${idx + 1}: ${cat || 'Category'}`;

        const newTable = doc.createElement('table');
        const thead = doc.createElement('thead');
        const headRow = doc.createElement('tr');
        keepHeaders.forEach((h) => {
          const th = doc.createElement('th');
          th.textContent = h;
          headRow.appendChild(th);
        });
        thead.appendChild(headRow);

        const tbody = doc.createElement('tbody');
        rowsForCat.forEach((vals, rowIdx) => {
          const tr = doc.createElement('tr');
          vals.forEach((val, colIdx) => {
            const td = doc.createElement('td');
            const htmlVal = byCategoryHtml[cat]?.[rowIdx]?.[colIdx] || val;
            td.innerHTML = htmlVal;
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });

        newTable.appendChild(thead);
        newTable.appendChild(tbody);

        fragment.appendChild(title);
        fragment.appendChild(newTable);
      });

    doc.body.innerHTML = '';
    doc.body.appendChild(fragment);
    return doc.body.innerHTML;
  } catch (err) {
    console.warn('transformCompetitorHtml failed', err);
    return html;
  }
};

const stripJsonFence = (text: string): string => {
  const fenceRegex = /^```(?:json)?\s*([\s\S]*?)```$/i;
  const match = fenceRegex.exec(text.trim());
  if (match) return match[1].trim();
  return text.trim();
};

const renderJsonTableIfPossible = (text: string): string | null => {
  const raw = stripJsonFence(text);
  try {
    const parsed = JSON.parse(raw);

    // Array of objects -> use first 3 keys
    if (Array.isArray(parsed) && parsed.length && typeof parsed[0] === 'object' && parsed[0] !== null) {
      const keys: string[] = Array.from(
        parsed.reduce<Set<string>>((acc, item) => {
          if (item && typeof item === 'object') {
            Object.keys(item).forEach((k) => acc.add(k));
          }
          return acc;
        }, new Set<string>())
      ).slice(0, 3);
      if (!keys.length) return null;

      const rows = parsed.map((item: any, idx: number) =>
        keys.map((k) => {
          const val = item?.[k];
          if (val === undefined || val === null) return '';
          if (Array.isArray(val)) return val.join(', ');
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        })
      );

      const headers = keys.map((k) => `<th>${k}</th>`).join('');
      const body = rows
        .map((cols) => `<tr>${cols.map((c) => `<td>${c}</td>`).join('')}</tr>`)
        .join('');
      return `<table><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table>`;
    }

    // Object -> convert to key/value rows, pad to 3 columns
    if (parsed && typeof parsed === 'object') {
      const entries = Object.entries(parsed).slice(0, 50);
      const headers = ['Field', 'Value', 'Extra'];
      const body = entries
        .map(([k, v]) => {
          const val =
            v === undefined || v === null
              ? ''
              : Array.isArray(v)
                ? v.join(', ')
                : typeof v === 'object'
                  ? JSON.stringify(v)
                  : String(v);
          return `<tr><td>${k}</td><td>${val}</td><td></td></tr>`;
        })
        .join('');
      return `<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table>`;
    }
  } catch (err) {
    return null;
  }
  return null;
};

const parseStructuredMarkdown = (
  text: string,
  opts?: { mode?: 'default' | 'competitor'; jsonTable?: boolean }
) => {
  if (!text || !text.trim()) return '';

  if (opts?.jsonTable) {
    const tableHtml = renderJsonTableIfPossible(text);
    if (tableHtml) {
      return sanitizeHtml(tableHtml);
    }
  }

  const rawHtml = marked.parse(text, { async: false }) as string;
  const sanitized = sanitizeHtml(rawHtml);
  if (opts?.mode === 'competitor') {
    return transformCompetitorHtml(sanitized);
  }
  return sanitized;
};

const StructuredMarkdown: React.FC<{ text: string; mode?: 'default' | 'competitor'; jsonTable?: boolean }> = ({
  text,
  mode = 'default',
  jsonTable = false,
}) => {
  const html = useMemo(() => parseStructuredMarkdown(text, { mode, jsonTable }), [text, mode, jsonTable]);
  if (!html) {
    return <>{text}</>;
  }
  return <div className="structured-markdown" dangerouslySetInnerHTML={{ __html: html }} />;
};

export type BriefBuilderHandle = {
  sendPrompt: (text: string) => Promise<void>;
  getStructuredHistory: () => {
    contexts: string[][];
    competitors: string[][];
    outputs: string[][];
  };
};

const BriefBuilderPanel = forwardRef<BriefBuilderHandle, BriefBuilderPanelProps>(
  (
    {
      onLaunchWorkspace,
      isLaunching,
      seedPrompt,
      hideInput = false,
      renderActions,
      renderSidebar,
      onConversationStart,
      onCompetitorStart,
      onOutputStart,
      sessionId,
      ensureSessionId,
      agentId,
      onDocumentsBusyChange,
    },
    ref
  ) => {
  const [briefId, setBriefId] = useState<string | null>(null);
  const [messages, setMessages] = useState<BriefBuilderMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextBlocks, setContextBlocks] = useState<string[]>([]);
  const [competitorBlocks, setCompetitorBlocks] = useState<string[]>([]);
  const [competitorStarted, setCompetitorStarted] = useState(false);
  const [outputBlocks, setOutputBlocks] = useState<string[]>([]);
  const [outputStarted, setOutputStarted] = useState(false);
  const [isScopeSyncedToDb, setIsScopeSyncedToDb] = useState(false);
  const [expandedCards, setExpandedCards] = useState<{context: boolean; competitor: boolean; output: boolean}>({
    context: true,
    competitor: true,
    output: true,
  });
  const { credits, isSignedIn, profile, refreshProfile } = useAuth();

  const highlightedContextBlocks = useDiffHighlights(
    contextBlocks,
    competitorStarted || outputStarted ? 'ctx-reset' : undefined
  );
  const highlightedCompetitorBlocks = useDiffHighlights(
    competitorBlocks,
    outputStarted ? 'comp-reset' : undefined
  );
  const highlightedOutputBlocks = useDiffHighlights(outputBlocks);
  const contextCompact = competitorStarted || outputStarted;
  const competitorCompact = outputStarted;
  const normalizedAgentId = (agentId || '').trim().toLowerCase().replace(/-/g, '_');
  const isMarketResearch = normalizedAgentId === 'market_research';
  useEffect(() => {
    // Debug signal to verify competitor rendering path and agent resolution
    // eslint-disable-next-line no-console
    console.log('[BriefBuilderPanel] competitor render', {
      agentId: agentId || null,
      normalizedAgentId,
      isMarketResearch,
      competitorBlockCount: competitorBlocks.length,
    });
  }, [isMarketResearch, normalizedAgentId, agentId, competitorBlocks.length]);

  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const seedTrackerRef = useRef<string | null>(null);
  const conversationStartedRef = useRef(false);
  const originalPromptRef = useRef<string>('');
  const outputSignalRef = useRef(false);
  const competitorSignalRef = useRef(false);
  const contextHistoryRef = useRef<string[][]>([]);
  const competitorHistoryRef = useRef<string[][]>([]);
  const outputHistoryRef = useRef<string[][]>([]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const latestAssistant = useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      const candidate = messages[idx];
      if (candidate.role === 'assistant') return candidate;
    }
    return null;
  }, [messages]);

  const extractTaggedPrompt = useCallback((text: string): string => {
    if (!text) return '';
    const startIdx = text.indexOf(PROMPT_START_TAG);
    const endIdx = text.indexOf(PROMPT_END_TAG);
    if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
      return text.slice(startIdx + PROMPT_START_TAG.length, endIdx).trim();
    }
    return '';
  }, []);

  const launchablePrompt = useMemo(() => {
    if (!latestAssistant) return '';
    return extractTaggedPrompt(latestAssistant.content);
  }, [latestAssistant, extractTaggedPrompt]);

  const latestStructuredBlocks = useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      const msg = messages[idx];
      if (msg.role !== 'assistant' || !msg.content) continue;
      const parsed = extractStructuredBlocks(msg.content);
      if (parsed.contexts.length || parsed.outputs.length) {
        return parsed;
      }
    }
    return { cleanedContent: '', contexts: [], competitors: [], outputs: [] };
  }, [messages]);

  const scrollThreadToBottom = useCallback(
    (opts?: { instant?: boolean; force?: boolean }) => {
      const container = chatScrollRef.current || threadRef.current;
      if (!container) return;
      const behavior = opts?.instant ? 'auto' : 'auto';
      const scrollOnce = () => {
        container.scrollTop = container.scrollHeight;
        container.scrollTo({ top: container.scrollHeight, behavior });
        if (threadEndRef.current) {
          threadEndRef.current.scrollIntoView({ behavior, block: 'end', inline: 'nearest' });
        }
      };
      scrollOnce();
      requestAnimationFrame(() => {
        scrollOnce();
        setTimeout(() => {
          scrollOnce();
          setTimeout(scrollOnce, 100);
        }, 80);
      });
    },
    []
  );

  const userScrolledUpRef = useRef(false);

  const handleScroll = useCallback(() => {
    const container = chatScrollRef.current;
    if (!container) return;
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    userScrolledUpRef.current = !isAtBottom;
  }, []);

  useEffect(() => {
    const container = chatScrollRef.current;
    const thread = threadRef.current;
    if (!container || !thread) return;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (!userScrolledUpRef.current) {
        scrollThreadToBottom({ instant: true });
      }
    });
    ro.observe(thread);
    return () => {
      ro.disconnect();
    };
  }, [scrollThreadToBottom]);

  useLayoutEffect(() => {
    if (!userScrolledUpRef.current) {
      scrollThreadToBottom({ instant: true });
    }
  }, [messages, sending, scrollThreadToBottom]);

  const resizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const minHeight = 48;
    const maxHeight = 240;
    el.style.height = 'auto';
    const nextHeight = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeInput();
  }, [inputValue, resizeInput]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending || isLaunching) return;
      setSending(true);
      setError(null);

      let activeSessionId = sessionId || null;
      if (!activeSessionId && ensureSessionId) {
        try {
          activeSessionId = await ensureSessionId();
        } catch (err) {
          console.error('Failed to initialize session for brief builder', err);
          setSending(false);
          setError('Unable to start a workspace session. Please try again.');
          return;
        }
      }
      if (!activeSessionId) {
        setSending(false);
        setError('Unable to start a workspace session. Please try again.');
        return;
      }
      const resolvedAgentId = (agentId || '').trim();
      if (!resolvedAgentId) {
        setSending(false);
        setError('Please select an agent before continuing.');
        return;
      }

      const timestamp = new Date().toISOString();
      let assistantIndex = -1;
      setMessages((prev) => {
        const userEntry: BriefBuilderMessage = {
          role: 'user',
          content: trimmed,
          timestamp,
        };
        const assistantEntry: BriefBuilderMessage = {
          role: 'assistant',
          content: '',
          timestamp: `${timestamp}-assistant`,
        };
        assistantIndex = prev.length + 1;
        return [...prev, userEntry, assistantEntry];
      });

      const applyToken = (chunk: string) => {
        if (!chunk) return;
        setMessages((prev) =>
          prev.map((msg, idx) =>
            idx === assistantIndex
              ? { ...msg, content: `${msg.content || ''}${chunk}` }
              : msg
          )
        );
      };

      const rollbackTempMessages = () => {
        setMessages((prev) => prev.slice(0, Math.max(0, prev.length - 2)));
      };

      try {
        const response = await apiService.streamBriefBuilderMessage(trimmed, {
          briefId: briefId || undefined,
          sessionId: activeSessionId || undefined,
          agentId: resolvedAgentId,
          onToken: applyToken,
        });
        setBriefId(response.brief_id);
        setInputValue('');
        setMessages((prev) =>
          prev.map((msg, idx) =>
            idx === assistantIndex ? { ...msg, content: response.reply || msg.content } : msg
          )
        );
      } catch (streamErr) {
        console.error('Brief builder streaming failed', streamErr);
        try {
          const fallback = await apiService.sendBriefBuilderMessage(trimmed, {
            briefId: briefId || undefined,
            sessionId: activeSessionId || undefined,
            agentId: resolvedAgentId,
          });
          setBriefId(fallback.brief_id);
          setMessages(fallback.messages);
          setInputValue('');
        } catch (err) {
          rollbackTempMessages();
          setError('Unable to reach our strategist. Please try again.');
        }
      } finally {
        setSending(false);
      }
    },
    [briefId, ensureSessionId, isLaunching, sending, sessionId]
  );

  const resetStructuredBlocks = useCallback(() => {
    setContextBlocks([]);
    setCompetitorBlocks([]);
    setCompetitorStarted(false);
    setOutputBlocks([]);
    setOutputStarted(false);
    contextHistoryRef.current = [];
    competitorHistoryRef.current = [];
    outputHistoryRef.current = [];
  }, []);

  const processPrompt = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (!conversationStartedRef.current) {
        setMessages([]);
        setBriefId(null);
        conversationStartedRef.current = true;
        originalPromptRef.current = trimmed;
        resetStructuredBlocks();
        if (onConversationStart) {
          onConversationStart();
        }
      }
      await sendMessage(trimmed);
    },
    [sendMessage, resetStructuredBlocks, onConversationStart]
  );

  const handleSend = useCallback(async () => {
    await processPrompt(inputValue);
  }, [inputValue, processPrompt]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    void handleSend();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const latestOutputScope = useMemo(
    () => tightenStructuredBlock(outputBlocks[outputBlocks.length - 1] || ''),
    [outputBlocks]
  );

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | null = null;
    let resolvedSessionId = (sessionId || '').trim() || null;

    if (!briefId || !latestOutputScope) {
      setIsScopeSyncedToDb(false);
      return () => {
        if (intervalId !== null) {
          window.clearInterval(intervalId);
        }
      };
    }

    // Reset to false whenever latest scope changes, then wait for DB convergence.
    setIsScopeSyncedToDb(false);

    const checkScopeSync = async () => {
      if (!resolvedSessionId && ensureSessionId) {
        try {
          resolvedSessionId = (await ensureSessionId()) || null;
        } catch (_err) {
          return;
        }
      }
      if (!resolvedSessionId || cancelled) {
        return;
      }

      try {
        const sections = await apiService.getReportSections(resolvedSessionId);
        const dbScope = tightenStructuredBlock((sections?.scope || '').trim());
        if (!cancelled) {
          setIsScopeSyncedToDb(Boolean(dbScope) && dbScope === latestOutputScope);
        }
      } catch (_err) {
        // Keep previous state on transient API/auth errors.
      }
    };

    void checkScopeSync();
    intervalId = window.setInterval(() => {
      void checkScopeSync();
    }, 2000);

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [briefId, latestOutputScope, sessionId, ensureSessionId]);

  // Show launch only when latest scope has been durably persisted in DB.
  const canLaunch = Boolean(briefId && latestOutputScope && isScopeSyncedToDb);

  const resolveLaunchPrompt = useCallback(() => {
    const fromTags = launchablePrompt.trim();
    if (fromTags) return fromTags;
    const fromInput = inputValue.trim();
    if (fromInput) return fromInput;
    return '';
  }, [inputValue, launchablePrompt]);

  const resolveSkipPrompt = useCallback(() => {
    const fromOriginal = originalPromptRef.current.trim();
    if (fromOriginal) return fromOriginal;
    const fromInput = inputValue.trim();
    if (fromInput) return fromInput;
    return '';
  }, [inputValue]);

  const handleLaunchClick = async () => {
    if (!briefId || !outputBlocks.length || sending || !isScopeSyncedToDb) return;
    setError(null);
    try {
      // Prefer tagged prompt, fall back to original prompt, then first scope
      let finalPrompt = resolveLaunchPrompt();
      if (!finalPrompt) {
        finalPrompt = originalPromptRef.current.trim();
      }
      if (!finalPrompt && outputBlocks.length > 0) {
        // Use first scope as fallback
        finalPrompt = outputBlocks[0].trim();
      }
      if (!finalPrompt) {
        setError('Please provide a final brief before launching.');
        return;
      }
      await onLaunchWorkspace({ briefId, finalPrompt });
    } catch (err) {
      console.error('Workspace launch failed', err);
      setError(err instanceof Error ? err.message : 'Unable to launch workspace right now.');
    }
  };

  const handleSkipLaunch = async () => {
    if (sending || isLaunching) return;
    setError(null);
    try {
      const skipPrompt = resolveSkipPrompt();
      if (!skipPrompt) {
        setError('Please provide a final brief before launching.');
        return;
      }
      await onLaunchWorkspace({ briefId: null, finalPrompt: skipPrompt });
    } catch (err) {
      console.error('Skip brief launch failed', err);
      setError(err instanceof Error ? err.message : 'Unable to launch workspace right now.');
    }
  };

  const copyBlockToClipboard = useCallback(async (text: string) => {
    if (!text) return;
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      console.warn('Clipboard API unavailable');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Unable to copy card content', err);
    }
  }, []);

  useEffect(() => {
    const trimmedSeed = (seedPrompt || '').trim();
    if (!trimmedSeed) {
      return;
    }
    if (seedTrackerRef.current === trimmedSeed) {
      return;
    }
    originalPromptRef.current = trimmedSeed;
    seedTrackerRef.current = trimmedSeed;
    setInputValue('');
    setMessages([]);
    setBriefId(null);
    conversationStartedRef.current = true;
    resetStructuredBlocks();
    void sendMessage(trimmedSeed);
  }, [seedPrompt, sendMessage, resetStructuredBlocks]);

  useEffect(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      const msg = messages[idx];
      if (msg.role !== 'assistant' || !msg.content) continue;
      const parsed = extractStructuredBlocks(msg.content);
      const hasStructured =
        parsed.contexts.length > 0 || parsed.competitors.length > 0 || parsed.outputs.length > 0;
      if (!hasStructured) continue;

      if (!outputStarted && parsed.contexts.length > 0 && !arraysEqual(parsed.contexts, contextBlocks)) {
        setContextBlocks(parsed.contexts);
        pushHistorySnapshot(contextHistoryRef, parsed.contexts);
      }

      if (parsed.competitors.length > 0) {
        if (!arraysEqual(parsed.competitors, competitorBlocks)) {
          setCompetitorBlocks(parsed.competitors);
          pushHistorySnapshot(competitorHistoryRef, parsed.competitors);
        }
        if (!competitorStarted) {
          setCompetitorStarted(true);
        }
      }

      if (parsed.outputs.length > 0) {
        if (!arraysEqual(parsed.outputs, outputBlocks)) {
          setOutputBlocks(parsed.outputs);
          pushHistorySnapshot(outputHistoryRef, parsed.outputs);
        }
        if (!outputStarted) {
          setOutputStarted(true);
        }
      } else if (!outputStarted && outputBlocks.length > 0) {
        setOutputBlocks([]);
      }
      return;
    }

    if (!outputStarted && outputBlocks.length > 0) {
      setOutputBlocks([]);
    }
  }, [messages, outputStarted, contextBlocks, competitorBlocks, competitorStarted, outputBlocks]);

  useEffect(() => {
    if (!onCompetitorStart) return;
    if (competitorStarted && !competitorSignalRef.current) {
      competitorSignalRef.current = true;
      onCompetitorStart();
    } else if (!competitorStarted) {
      competitorSignalRef.current = false;
    }
  }, [competitorStarted, onCompetitorStart]);

  useEffect(() => {
    if (!onOutputStart) return;
    if (outputStarted && !outputSignalRef.current) {
      outputSignalRef.current = true;
      onOutputStart();
    } else if (!outputStarted) {
      outputSignalRef.current = false;
    }
  }, [outputStarted, onOutputStart]);

  useImperativeHandle(
    ref,
    () => ({
      sendPrompt: async (text: string) => {
        await processPrompt(text);
      },
      getStructuredHistory: () => ({
        contexts: contextHistoryRef.current.map((snapshot) => [...snapshot]),
        competitors: competitorHistoryRef.current.map((snapshot) => [...snapshot]),
        outputs: outputHistoryRef.current.map((snapshot) => [...snapshot]),
      }),
    }),
    [processPrompt]
  );

  const hasSidebarContent =
    contextBlocks.length > 0 ||
    competitorBlocks.length > 0 ||
    competitorStarted ||
    outputBlocks.length > 0 ||
    outputStarted;

  const briefBuilderStarted = messages.length > 0;



  const toggleCard = useCallback((card: 'context' | 'competitor' | 'output') => {
    setExpandedCards(prev => ({ ...prev, [card]: !prev[card] }));
  }, []);

  const sidebar = (
    <aside className="brief-structured-sidebar">
      {hasSidebarContent && contextBlocks.length > 0 && (
        <div
          className={`brief-structured-card brief-structured-card--context ${
            contextCompact ? 'brief-structured-card--compact' : ''
          } ${expandedCards.context ? '' : 'brief-structured-card--collapsed'}`}
        >
          <button
            type="button"
            className="brief-structured-card__title brief-structured-card__title--toggle"
            onClick={() => toggleCard('context')}
            aria-expanded={expandedCards.context}
          >
            <span>Research Context</span>
            {expandedCards.context ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expandedCards.context && contextBlocks.map((ctx, ctxIdx) => (
            <div key={`latest-context-${ctxIdx}`} className="brief-structured-card__content">
              <button
                type="button"
                className="structured-copy-btn"
                onClick={() => copyBlockToClipboard(ctx)}
                aria-label="Copy context block"
              >
                <Copy size={12} strokeWidth={2} />
              </button>
              <StructuredMarkdown text={highlightedContextBlocks[ctxIdx] || ctx} />
            </div>
          ))}
        </div>
      )}
      {(competitorStarted || competitorBlocks.length > 0) && (
        <div
          className={`brief-structured-card brief-structured-card--context brief-structured-card--competitor ${
            competitorCompact ? 'brief-structured-card--compact' : ''
          } ${expandedCards.competitor ? '' : 'brief-structured-card--collapsed'}`}
        >
          <button
            type="button"
            className="brief-structured-card__title brief-structured-card__title--toggle"
            onClick={() => toggleCard('competitor')}
            aria-expanded={expandedCards.competitor}
          >
            <span>{getAgentLanguage(agentId).competitorHeading}</span>
            {expandedCards.competitor ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expandedCards.competitor && (
            competitorBlocks.length > 0 ? (
              competitorBlocks.map((comp, compIdx) => (
                <div key={`latest-competitor-${compIdx}`} className="brief-structured-card__content">
                  <button
                    type="button"
                    className="structured-copy-btn"
                    onClick={() => copyBlockToClipboard(comp)}
                    aria-label="Copy competitor block"
                  >
                    <Copy size={12} strokeWidth={2} />
                  </button>
                  <StructuredMarkdown
                    text={highlightedCompetitorBlocks[compIdx] || comp}
                    mode={isMarketResearch ? 'default' : 'competitor'}
                    jsonTable={isMarketResearch}
                  />
                </div>
              ))
            ) : (
              <div className="brief-structured-card__content text-slate-500">Capturing competitors…</div>
            )
          )}
        </div>
      )}
      {outputStarted && (
        <div className={`brief-structured-card brief-structured-card--output ${expandedCards.output ? '' : 'brief-structured-card--collapsed'}`}>
          <button
            type="button"
            className="brief-structured-card__title brief-structured-card__title--toggle"
            onClick={() => toggleCard('output')}
            aria-expanded={expandedCards.output}
          >
            <span>Research Scope</span>
            {expandedCards.output ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {expandedCards.output && (
            outputBlocks.length > 0 ? (
              outputBlocks.map((out, outIdx) => (
                <div key={`latest-output-${outIdx}`} className="brief-structured-card__content">
                  <button
                    type="button"
                    className="structured-copy-btn"
                    onClick={() => copyBlockToClipboard(out)}
                    aria-label="Copy scope block"
                  >
                    <Copy size={12} strokeWidth={2} />
                  </button>
                  <StructuredMarkdown text={highlightedOutputBlocks[outIdx] || out} />
                </div>
              ))
            ) : (
              <div className="brief-structured-card__content text-slate-500">Awaiting first output…</div>
            )
          )}
        </div>
      )}
    </aside>
  );

  const externalSidebar = renderSidebar ? renderSidebar(sidebar) : null;
  const inlineSidebar = renderSidebar ? null : (hasSidebarContent || isSignedIn) ? sidebar : null;

  return (
    <>
      {externalSidebar}
      <div className="brief-panel-canvas">
        <div className={`brief-status-arrow ${briefBuilderStarted ? 'brief-status-arrow--full-width' : ''}`}>
          <div className="brief-status-arrow__line" />
        </div>
        <DocumentUploadBar
          briefId={briefId}
          sessionId={sessionId}
          ensureSessionId={ensureSessionId}
          onBusyChange={onDocumentsBusyChange}
        />
        <div className="brief-panel-layout">
          <div className="brief-panel brief-panel--flat">
            <div className="brief-panel__body brief-panel__body--chat">
              <div className="brief-panel__messages-scroll" ref={chatScrollRef}>
                <div ref={threadRef} className="brief-thread">
                  {messages.map((msg, idx) => {
                    const isAssistant = msg.role === 'assistant';
                    const structured = isAssistant ? extractStructuredBlocks(msg.content || '') : null;
                    const contentToShow = isAssistant ? structured?.cleanedContent || '' : msg.content || '';

                    return (
                      <div
                        key={`${msg.timestamp}-${idx}`}
                        className={`brief-message brief-message--${msg.role} brief-message--plain`}
                      >
                        <div className="brief-message__label">
                          {msg.role === 'user' ? 'You' : 'Astra'}
                        </div>

                        <div
                          className={`brief-message__content ${
                            isAssistant && !contentToShow ? 'brief-message__content--empty' : ''
                          }`}
                        >
                          {isAssistant && contentToShow ? (
                            <ChatMarkdown text={contentToShow} className="structured-markdown" />
                          ) : (
                            contentToShow
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {sending && (
                    <div className="brief-message brief-message--assistant brief-message--typing brief-message--plain">
                      <Loader2 className="brief-spinner" />
                      <span>Thinking...</span>
                    </div>
                  )}
                  <div ref={threadEndRef} className="brief-thread__end" />
                </div>
              </div>

              {canLaunch && (
                <div className="brief-panel__launch-area">
                  <button
                    type="button"
                    className="brief-launch-btn brief-launch-btn--primary"
                    onClick={handleLaunchClick}
                    disabled={sending || isLaunching}
                  >
                    <span>Launch Workspace</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              )}
              {!canLaunch && briefId && latestOutputScope && (
                <div className="brief-panel__launch-area">
                  <button
                    type="button"
                    className="brief-launch-btn brief-launch-btn--primary"
                    disabled
                  >
                    <Loader2 size={18} className="brief-spinner" />
                    <span>Saving scope…</span>
                  </button>
                </div>
              )}

              {hideInput && error && <div className="brief-error">{error}</div>}
            </div>
          </div>

          {inlineSidebar}
        </div>
      </div>
    </>
  );
});

BriefBuilderPanel.displayName = 'BriefBuilderPanel';

export default BriefBuilderPanel;

